import { Directive, ElementRef, HostListener, Input, Self, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appOnlyNumbers]'
})
export class OnlyNumbersDirective {

  @Input() allowDecimals: boolean = false; // Por defecto solo números enteros
  @Input() maxDecimals: number = 2; // Máximo de decimales permitidos

  constructor(
    private el: ElementRef,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this.el.nativeElement as HTMLInputElement;
    const key = event.key;
    const currentValue = input.value;

    // Permitir teclas de control
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End' ||
      (event.ctrlKey && (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'A' || key === 'C' || key === 'V' || key === 'X'))
    ) {
      return;
    }

    // Si permite decimales, permitir punto o coma (convertir coma a punto)
    if (this.allowDecimals && (key === '.' || key === ',')) {
      // Solo permitir un punto decimal
      if (currentValue.includes('.')) {
        event.preventDefault();
        return;
      }

      // Convertir coma a punto
      if (key === ',') {
        event.preventDefault();
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = currentValue.substring(0, start) + '.' + currentValue.substring(end);
        input.value = newValue;
        input.setSelectionRange(start + 1, start + 1);

        // Actualizar FormControl
        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(newValue, { emitEvent: true });
        }
      }
      return;
    }

    // Solo permitir números (0-9)
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }

    // Si permite decimales, validar cantidad de decimales
    if (this.allowDecimals && currentValue.includes('.')) {
      const cursorPosition = input.selectionStart || 0;
      const decimalPosition = currentValue.indexOf('.');

      // Si el cursor está después del punto
      if (cursorPosition > decimalPosition) {
        const decimalsAfterPoint = currentValue.substring(decimalPosition + 1);

        // Si ya tiene el máximo de decimales, no permitir más
        if (decimalsAfterPoint.length >= this.maxDecimals && input.selectionStart === input.selectionEnd) {
          event.preventDefault();
        }
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';

    let processedText = '';

    if (this.allowDecimals) {
      // Permitir números y un solo punto decimal
      // Reemplazar coma por punto
      processedText = pastedText.replace(/,/g, '.');

      // Eliminar todo excepto números y punto
      processedText = processedText.replace(/[^\d.]/g, '');

      // Asegurar que solo haya un punto
      const parts = processedText.split('.');
      if (parts.length > 2) {
        processedText = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limitar decimales
      if (parts.length === 2 && parts[1].length > this.maxDecimals) {
        processedText = parts[0] + '.' + parts[1].substring(0, this.maxDecimals);
      }
    } else {
      // Solo números enteros
      processedText = pastedText.replace(/\D/g, '');
    }

    const input = this.el.nativeElement as HTMLInputElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = input.value;

    const newValue = currentValue.substring(0, start) + processedText + currentValue.substring(end);
    input.value = newValue;

    // Actualizar FormControl
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(newValue, { emitEvent: true });
    }

    // Posicionar cursor después del texto pegado
    const newPosition = start + processedText.length;
    input.setSelectionRange(newPosition, newPosition);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent): void {
    const input = this.el.nativeElement as HTMLInputElement;
    let value = input.value;

    // Si permite decimales y termina con punto, eliminarlo
    if (this.allowDecimals && value.endsWith('.')) {
      value = value.slice(0, -1);
      input.value = value;

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(value, { emitEvent: true });
      }
    }
  }
}
